import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
} from '@angular/core';
import { RowStatus } from '@app/models/status.model';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import {
  catchError,
  finalize,
  from,
  isObservable,
  Observable,
  tap,
  throwError,
} from 'rxjs';

type DeleteCallback<TData = unknown> = (
  rowId: string,
  data?: TData
) => void | Promise<void> | Observable<unknown>;

type DeleteCellParams<TData = unknown> = ICellRendererParams & {
  status?: RowStatus;
  onDelete?: DeleteCallback<TData>;
};

@Component({
  selector: 'app-delete-cell-renderer',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TranslateModule],
  templateUrl: './delete-cell-renderer.component.html',
  styleUrl: './delete-cell-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteCellRendererComponent implements ICellRendererAngularComp {
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  private readonly statusSig = signal<RowStatus>('pending');
  @Input() set status(status: RowStatus) {
    this.statusSig.set(status ?? 'pending');
  }
  get status(): RowStatus {
    return this.statusSig();
  }

  readonly isLoading = signal(false);

  private rowId = '';
  private rowData: unknown;
  private onDeleteCb?: DeleteCallback<unknown>;

  agInit(params: DeleteCellParams): void {
    this.rowId = String(params.data?.id ?? '');
    this.statusSig.set(
      params.status ?? (params.data as any)?.docStatus ?? 'pending'
    );
    this.rowData = params.data;
    this.onDeleteCb = params.onDelete;
  }

  refresh(): boolean {
    return false;
  }

  onDelete(): void {
    if (!this.onDeleteCb || !this.rowId || this.isLoading()) return;

    try {
      const result = this.onDeleteCb(this.rowId, this.rowData);
      const obs: Observable<unknown> = isObservable(result)
        ? (result as Observable<unknown>)
        : from(Promise.resolve(result as void));

      this.isLoading.set(true);
      obs
        .pipe(
          tap(() =>
            this.toast.success(
              this.translate.instant('admin.toasts.deletedSuccess')
            )
          ),
          catchError((err) => {
            this.toast.error(
              this.translate.instant('admin.toasts.deleteFailed')
            );
            return throwError(() => err);
          }),
          finalize(() => this.isLoading.set(false))
        )
        .subscribe();
    } catch {
      this.toast.error(this.translate.instant('admin.toasts.deleteFailed'));
    }
  }
}
